// Full-document shell for cocoon pages rendered entirely through React.
// reactivated's renderer injects the bundle CSS/JS + __PRELOADED_PROPS__, so we
// deliberately emit none of that here.
import { Immutable } from "immer";
import React from "react";
import { FlashMessages } from "tsi-common-react/src/components/Message/index";
import { MetaTags } from "tsi-common-react/src/components/MetaTags/index";
import { StickyEmailBanner } from "tsi-common-react/src/components/StickyEmailBanner/index";
import {
    initPromoCalloutMessages,
    useFocusFirstFormError,
    useLinkRewrites,
    useRichTextDecorators,
} from "tsi-common-react/src/utils/richTextDecorators";
import { watchForWistia } from "tsi-common-react/src/utils/wistia";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { ExternalScript } from "@thelabnyc/thelabui/src/components/ExternalScript";
import { useSSRNonce } from "@thelabnyc/thelabui/src/hooks/useSSRNonce";
import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as FinancingModal } from "../../templates/FinancingModalTemplate";
import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";
import { initCompareShippingLocalization } from "../../utils/compareLocalization";
import { PromoModal, PromoModalProvider } from "../PromoModal";
import { SiteFooter } from "../SiteFooter";
import { SiteHeader } from "../SiteHeader";

type IMessage =
    templates.UtilityPageTemplate["page_chrome"]["messages"][number];

// Server-rendered Django flash messages, rendered + dismissed by the shared
// FlashMessages component (which owns per-item dismissal). This wraps it with
// the cocoon close icon and the no-JS fallback banner.
const Messages = ({
    messages,
}: {
    messages: readonly IMessage[];
}): React.JSX.Element => (
    <>
        {messages.length > 0 && (
            <FlashMessages
                closeIcon={
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <title>Close</title>
                        <path d="M6 6l12 12M18 6L6 18" />
                    </svg>
                }
                messages={messages.map((message) => ({
                    tags: message.tags,
                    content: (
                        <p>
                            <span
                                className={`messages__item__category messages__item__category--${message.level_tag}`}
                            >
                                {message.category_label}
                            </span>{" "}
                            {message.is_html ? (
                                // Only messages explicitly mark_safe'd
                                // server-side render as HTML; plain
                                // messages are auto-escaped.
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: message.text,
                                    }}
                                />
                            ) : (
                                <span>{message.text}</span>
                            )}
                        </p>
                    ),
                }))}
            />
        )}
        <noscript
            dangerouslySetInnerHTML={{
                // Built at render time so `t` resolves against the active
                // request locale (a module-scope literal would freeze to the
                // import-time locale under SSR).
                __html: `<aside class="messages l-full-width"><ul class="messages__list"><li class="messages__item error"><div class="messages__item__content l-capped-width"><p><span class="messages__item__category messages__item__category--error">${t`Error`}</span>${t`Please enable Javascript`}</p></div></li></ul></aside>`,
            }}
        />
    </>
);

// The sticky email-signup banner reads localStorage in a useState initializer
// to decide whether it was already dismissed, so its first render differs
// between the server (no localStorage -> shown) and a returning client (shown
// -> null). Gate it to client-only paint to keep server + first-hydration
// output identical; the banner itself is only revealed on scroll, so nothing
// visible is lost by not server-rendering it.
const ClientOnlyStickyEmailBanner = ({
    bannerJson,
}: {
    bannerJson: string;
}): React.ReactNode => {
    const [isHydrated, setIsHydrated] = React.useState(false);
    React.useEffect(() => setIsHydrated(true), []);
    if (!isHydrated) {
        return null;
    }
    return <StickyEmailBanner bannerData={JSON.parse(bannerJson)} />;
};

// Google Seller Ratings badge, DNT-gated like its {% jstracker %} wrapper.
// Rendered client-only: the inline renderBadge definition would be a
// positionally-tracked React body vnode and could be paired against a GTM-
// injected foreign <script>, tripping a hydration mismatch. Define the
// callback in useEffect (post-hydration), then inject the platform.js loader.
type IGapi = {
    load: (name: string, cb: () => void) => void;
    ratingbadge?: {
        render: (el: HTMLElement, opts: { merchant_id: number }) => void;
    };
};

const SellerBadge = (): React.ReactNode => {
    React.useEffect(() => {
        const win = getWindow() as
            | (Window & {
                  renderBadge?: () => void;
                  gapi?: IGapi;
              })
            | null;
        const doc = getDocument();
        if (!win || !doc) return;
        win.renderBadge = function () {
            const ratingBadgeContainer = doc.createElement("div");
            doc.body.appendChild(ratingBadgeContainer);
            win.gapi?.load("ratingbadge", function () {
                win.gapi?.ratingbadge?.render(ratingBadgeContainer, {
                    merchant_id: 111359840,
                });
            });
        };
        const script = doc.createElement("script");
        script.src =
            "https://apis.google.com/js/platform.js?onload=renderBadge";
        script.async = true;
        script.defer = true;
        doc.body.appendChild(script);
    }, []);
    return null;
};

// The header/footer chrome forwarded into SiteHeader -> RichNavigation comes
// from Context (mutable reactivated props), not from these IProps, so the
// per-page pageChrome prop is held Immutable<> like all other React props.
type IProps = {
    pageChrome: Immutable<templates.UtilityPageTemplate["page_chrome"]>;
    children?: React.ReactNode;
    showHeader?: boolean;
    showFooter?: boolean;
    wrapMain?: boolean;
    // Rendered after the footer, before the promo modal — cocoon.html renders a
    // page's fine_print there, inside #content.
    afterFooter?: React.ReactNode;
    // Strip a full page's tracking/consent/modal furniture (GTM, OneTrust,
    // JSON-LD, session keep-alive, financing/promo modals, seller badge, flash
    // messages). For embedded fragments (nav/footer iframes) that are not
    // standalone pages: the legacy iframe templates already suppressed OneTrust,
    // and a hidden fragment must not fire GTM conversion tags -- under
    // full-document hydration those inject foreign <script>s into the
    // React-owned body and trip a mismatch.
    bareChrome?: boolean;
};

const UnsupportedWarning = ({
    staticBase,
}: {
    staticBase: string;
}): React.JSX.Element => {
    const browsers = [
        {
            title: t`Get Google Chrome`,
            href: "http://www.google.com/chrome",
            img: "tsicommon_cms/img/chrome_32x32.png",
            label: t`Google Chrome`,
        },
        {
            title: t`Get Mozilla Firefox`,
            href: "http://www.firefox.com",
            img: "tsicommon_cms/img/firefox_32x32.png",
            label: t`Mozilla Firefox`,
        },
        {
            title: t`Get Apple Safari`,
            href: "http://www.apple.com/safari/download",
            img: "tsicommon_cms/img/safari_32x32.png",
            label: t`Apple Safari`,
        },
        {
            title: t`Get Microsoft Edge`,
            href: "https://www.microsoft.com/en-us/edge",
            img: "tsicommon_cms/img/edge_32x32.png",
            label: t`Microsoft Edge`,
        },
    ];
    return (
        <>
            <div className="unsupported-browser-container">
                <div className="unsupported-browser">
                    {}
                    <a
                        className="unsupported-browser__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-browser__title">
                        {t`Improve Your Shopping Experience`}
                    </div>
                    <p className="unsupported-browser__copy">
                        {t`You’re using an unsupported web browser.`}
                        <br />
                        {t`A list of the most popular browsers can be found below. Just click on an icon to visit the download page.`}
                    </p>
                    <ul className="unsupported-browser__list">
                        {browsers.map((b) => (
                            <li
                                className="unsupported-browser__list-item"
                                key={b.img}
                            >
                                <a
                                    title={b.title}
                                    href={b.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={`${staticBase}${b.img}`}
                                        alt={b.title}
                                        title={b.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <br />
                                    {b.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="unsupported-mobileos-container">
                <div className="unsupported-mobileos">
                    <a
                        className="unsupported-mobileos__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-mobileos__title">
                        {t`Your Software is Out of Date`}
                    </div>
                    <p className="unsupported-mobileos__copy">
                        {t`Your phone or tablet is using software unsupported by our website. To improve your shopping experience, please update your device to the latest version of iOS or Android OS.`}
                    </p>
                </div>
            </div>
        </>
    );
};

export const PageSkeleton = (props: IProps): React.JSX.Element => {
    React.useEffect(() => watchForWistia(), []);
    const {
        pageChrome,
        children,
        showHeader,
        showFooter,
        wrapMain,
        afterFooter,
        bareChrome,
    } = props;
    // Global chrome (header/nav, footer, social, promo modal, messages, head/
    // body globals) comes from the `chrome` context processor. PageSkeleton is
    // always the full-document root inside the reactivated Provider, so the
    // context is populated in both SSR and client hydration.
    const context = React.useContext(Context);
    const chrome = context.chrome;
    const nonce = useSSRNonce();
    // Whether the promo modal is rendered on this page (matches the JSX gate in
    // the footer block below); the PromoModalProvider's auto-open gate is a
    // no-op when it is not present.
    const showPromoModal =
        showFooter !== false && !bareChrome && chrome.show_promo_modal;
    // Post-hydration DOM decorators shared by every full-document page: fill CMS
    // rich-text promo tokens, fill the Casper-compare static shipping-estimator
    // placeholders (a no-op off compare pages), apply the link-rewrite ruleset,
    // and focus the first form error. initCompareShippingLocalization handles the
    // async shipping fetch and re-fills when the shared broker reports a new
    // location -- there is no useRichTextDecorators re-run to depend on.
    useRichTextDecorators([
        initPromoCalloutMessages,
        initCompareShippingLocalization,
    ]);
    useLinkRewrites();
    useFocusFirstFormError();
    // `data-useragent` on <html> drives the unsupported-browser CSS
    // (html[data-useragent*="MSIE 10.0"], etc.). It's per-user and these pages
    // are cacheable, so it must NOT be server-rendered; and in this
    // full-document render React owns <html>, so an early imperative set would
    // race hydration and mismatch. Set it here, after hydration -- React owns
    // <html> but never carries the attribute in its vdom, so there is nothing to
    // reconcile. legacy.js still sets it for old browsers that never boot React
    // (see legacy.tsx's module-support guard).
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (win?.navigator && doc?.documentElement) {
            doc.documentElement.setAttribute(
                "data-useragent",
                win.navigator.userAgent,
            );
        }
        // Any touchstart listener enables CSS :hover on iOS. Unlike an
        // ontouchstart attribute, a listener needs no CSP hash.
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        const noop = () => {};
        doc?.body.addEventListener("touchstart", noop, { passive: true });
        return () => doc?.body.removeEventListener("touchstart", noop);
    }, []);

    // The OneTrust callback stub stays out of the render tree: it only has to
    // exist before the async SDK finishes loading and calls it, so defining it
    // post-hydration keeps the GTM bootstrap the first script vnode in <body>.
    React.useEffect(() => {
        if (bareChrome) return;
        const win = getWindow() as
            | (Window & { OptanonWrapper?: () => void })
            | null;
        if (!win) return;
        if (!("OptanonWrapper" in win)) {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            win.OptanonWrapper = function () {};
        }
    }, [bareChrome]);

    // Page-level layout writers, all reading the live DOM so they run
    // client-only after hydration (the custom properties fall back to their SCSS
    // defaults until then): the configurator upsell-modal panel offset (tracks
    // the top of #main-content, clamped to 0 once scrolled past it, recomputed on
    // scroll) and the one-shot product-hero sticky offset. Plus the deep-link
    // affordance: if the URL carries a hash, open the targeted accordion (toggle
    // `active` on it and its sibling) and scroll it into view.
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (!win || !doc) {
            return;
        }
        const root = doc.documentElement;
        const mainContent = doc.getElementById("main-content");
        const setUpsellPanelOffset = () => {
            if (!mainContent) {
                return;
            }
            const top = mainContent.getBoundingClientRect().top;
            root.style.setProperty(
                "--configurator--upsell--panel-offset",
                top < 0 ? "0px" : `${top}px`,
            );
        };
        if (mainContent) {
            root.style.setProperty(
                "--product-hero--sticky-offset",
                `${mainContent.getBoundingClientRect().top}px`,
            );
        }
        setUpsellPanelOffset();
        const onScroll = () => {
            setUpsellPanelOffset();
        };
        win.addEventListener("scroll", onScroll);
        if (win.location.hash) {
            // getElementById (not querySelector): the intent is an ID match,
            // and a hash that isn't a valid CSS selector (e.g. `#123`, or one
            // with `.`/`:`) makes querySelector throw a SyntaxError -- which,
            // firing before the return below, would skip cleanup and leak the
            // scroll listener.
            const targetElem = doc.getElementById(win.location.hash.slice(1));
            if (targetElem) {
                targetElem.classList.toggle("active");
                targetElem.nextElementSibling?.classList.toggle("active");
                targetElem.scrollIntoView();
            }
        }
        return () => {
            win.removeEventListener("scroll", onScroll);
        };
    }, []);

    return (
        <PromoModalProvider present={showPromoModal}>
            <html
                className="no-js"
                lang={chrome.html_lang}
                // Static signal that this is a full-document React render (React owns
                // <html>). legacy.js reads it to decide whether to set data-useragent
                // itself -- it must be an attribute parsed up-front, NOT the
                // __PRELOADED_PROPS__ window global, because legacy.js loads async
                // (hoistable, collision-proof) and may run before that global exists.
                data-react-document="true"
            >
                <head>
                    {/*
                     * cocoon.html uses an inline `html{visibility:hidden;opacity:0}`
                     * FOUC guard, relying on the bundle CSS (`html{visibility:visible}`)
                     * overriding it because the bundle <link> comes AFTER it in source
                     * order. In a full-document React render, React 19 hoists the bundle
                     * stylesheet to the TOP of <head> (before any inline <style>), which
                     * would invert that order and leave the page permanently hidden. The
                     * bundle <link> is render-blocking in <head> here, so there is no
                     * FOUC to guard against -- we intentionally omit the inline hide.
                     */}
                    <title>{pageChrome.title}</title>
                    <meta
                        httpEquiv="content-type"
                        content="text/html; charset=UTF-8"
                    />
                    <meta name="created" content={chrome.created} />
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1.0"
                    />
                    <meta
                        name="description"
                        content={pageChrome.search_description}
                    />
                    <meta
                        name="google-site-verification"
                        content={chrome.google_site_verification}
                    />
                    {pageChrome.search_keywords && (
                        <meta
                            name="keywords"
                            content={pageChrome.search_keywords}
                        />
                    )}
                    {pageChrome.robots_noindex && (
                        <meta name="robots" content="noindex" />
                    )}
                    <link rel="canonical" href={pageChrome.canonical_url} />
                    <link
                        rel="shortcut icon"
                        type="image/png"
                        href={chrome.favicon_url}
                    />
                    <meta property="og:image" content={chrome.og_image_url} />
                    {/* JSON-LD ({% google_page_schema %}) is rendered in <body>
                    as opaque <div> innerHTML -- see the body comment. */}
                </head>
                <body
                    id={chrome.body_id}
                    className={pageChrome.body_class}
                    data-appslug={chrome.app_slug}
                    data-version={chrome.version}
                    data-environment={chrome.environment}
                    data-sentry-dsn={chrome.sentry_dsn}
                    data-reviews-api={chrome.reviews_api}
                    data-static-base={chrome.static_base}
                    data-track-page-type={pageChrome.page_type}
                    data-view-name={chrome.view_name}
                >
                    {chrome.aws_waf_url && (
                        <ExternalScript src={chrome.aws_waf_url} defer={true} />
                    )}
                    {/*
                     * Top of <body>, not <head>: React 19 hoists the <meta> and
                     * async <script src> children up, while an inline <script>
                     * in the React-owned <head> gets paired with whatever a
                     * third party injects there before hydration finishes.
                     */}
                    {!bareChrome && (
                        <MetaTags
                            tags={context.meta_tags.meta_tags.value}
                            doNotTrack={chrome.do_not_track}
                        />
                    )}
                    {/*
                     * JSON-LD schemas are emitted as SSR-only markup: the raw HTML
                     * string is inserted as a <div>'s innerHTML, so the <script
                     * type="application/ld+json"> tags appear in the server-rendered
                     * HTML for crawlers but are never React vnodes and are never
                     * positionally tracked in the hydration tree.
                     *
                     * Scripts inserted via innerHTML do not execute in browsers
                     * (HTML5 spec §4.12.1 "scripting" / dynamic-insert rule), so
                     * these tags carry data only -- exactly what JSON-LD requires.
                     */}
                    {!bareChrome && pageChrome.json_ld.length > 0 && (
                        <div
                            dangerouslySetInnerHTML={{
                                __html: pageChrome.json_ld
                                    .map(
                                        (schema) =>
                                            `<script type="application/ld+json">${schema}</script>`,
                                    )
                                    .join(""),
                            }}
                        />
                    )}
                    <div id="content">
                        {showHeader !== false && (
                            <div className="main-header">
                                <SiteHeader {...chrome.site_header} />
                                {!bareChrome && (
                                    <Messages messages={pageChrome.messages} />
                                )}
                            </div>
                        )}
                        {wrapMain !== false ? (
                            <main id="main-content">{children}</main>
                        ) : (
                            children
                        )}
                        {showFooter !== false && (
                            <>
                                <SiteFooter
                                    social={chrome.social}
                                    copyrightYear={chrome.copyright_year}
                                    staticBase={chrome.static_base}
                                    footerNav={chrome.footer_nav}
                                />
                                {afterFooter}
                                {showPromoModal && (
                                    <PromoModal
                                        settings={chrome.promo_modal}
                                        staticBase={chrome.static_base}
                                    />
                                )}
                            </>
                        )}
                        {!bareChrome && pageChrome.sticky_email_banner_json && (
                            <ClientOnlyStickyEmailBanner
                                bannerJson={pageChrome.sticky_email_banner_json}
                            />
                        )}
                    </div>
                    {!bareChrome && <SessionKeepAlive />}
                    {!bareChrome && <FinancingModal />}
                    {!bareChrome &&
                        chrome.show_seller_badge &&
                        !chrome.do_not_track && <SellerBadge />}
                    {/* async (not defer) so React 19 treats this as a hoistable
                    resource -- lifted to <head> and matched by src, never paired
                    positionally against a sibling. Tag managers (GTM) inject
                    conversion <script>s into <body> around hydration time; a
                    body-positional script here gets paired with a foreign node
                    and trips a hydration mismatch. legacy.tsx is order-
                    independent and a no-op on module-capable browsers, so async
                    is semantically free. */}
                    <script
                        async={true}
                        src={`${chrome.static_base}dist2/legacy.js`}
                        nonce={nonce}
                        suppressHydrationWarning={true}
                    />
                    <UnsupportedWarning staticBase={chrome.static_base} />
                </body>
            </html>
        </PromoModalProvider>
    );
};
